import React, { useState } from "react";
import { DayPicker, DateRange } from "react-day-picker";
import { pl } from "date-fns/locale";
import "react-day-picker/dist/style.css";

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  className?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  className = "",
}) => {
  const [mode, setMode] = useState<"start" | "end">("start");

  const startDateObj = startDate ? new Date(startDate) : undefined;
  const endDateObj = endDate ? new Date(endDate) : undefined;

  const range: DateRange | undefined =
    startDateObj && endDateObj
      ? { from: startDateObj, to: endDateObj }
      : startDateObj
        ? { from: startDateObj, to: undefined }
        : undefined;

  const formatDateToString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const dayOfMonth = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${dayOfMonth}`;
  };

  const handleDayClick = (day: Date) => {
    const dayString = formatDateToString(day);

    if (mode === "start") {
      let newStartDate = dayString;
      let newEndDate = endDate;

      // Jeśli jest już data końcowa i nowa data początkowa jest późniejsza, zamień je miejscami
      if (endDate && new Date(dayString) > new Date(endDate)) {
        newStartDate = endDate;
        newEndDate = dayString;
      }

      onChange(newStartDate, newEndDate);
      setMode("end");
    } else {
      let newStartDate = startDate;
      let newEndDate = dayString;

      // Jeśli jest już data początkowa i nowa data końcowa jest wcześniejsza, zamień je miejscami
      if (startDate && new Date(dayString) < new Date(startDate)) {
        newStartDate = dayString;
        newEndDate = startDate;
      }

      onChange(newStartDate, newEndDate);
      setMode("start");
    }
  };

  const handleModeToggle = () => {
    setMode(mode === "start" ? "end" : "start");
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "Wybierz datę";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getDuration = () => {
    if (!startDate || !endDate) return "";
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays === 1 ? "1 dzień" : `${diffDays} dni`;
  };

  return (
    <div
      className={`bg-white rounded-xl border border-neutral-200 shadow-lg ${className}`}
    >
      {/* Header z trybem wyboru */}
      <div className="px-6 py-4 border-b border-neutral-100 bg-gradient-to-r from-primary-50 to-primary-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-neutral-900">
            Wybierz termin pobytu
          </h3>
          <div className="text-sm text-primary-600 font-medium">
            {getDuration()}
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            type="button"
            onClick={handleModeToggle}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              mode === "start"
                ? "bg-primary-600 text-white shadow-md"
                : "bg-white text-primary-600 border border-primary-200 hover:bg-primary-50"
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <div>
                <div className="font-medium">Data przyjazdu</div>
                <div className="text-xs opacity-80">
                  {formatDate(startDate)}
                </div>
              </div>
            </div>
          </button>

          <div className="flex items-center px-2">
            <svg
              className="w-5 h-5 text-neutral-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </div>

          <button
            type="button"
            onClick={handleModeToggle}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              mode === "end"
                ? "bg-primary-600 text-white shadow-md"
                : "bg-white text-primary-600 border border-primary-200 hover:bg-primary-50"
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <div>
                <div className="font-medium">Data wyjazdu</div>
                <div className="text-xs opacity-80">{formatDate(endDate)}</div>
              </div>
            </div>
          </button>
        </div>

        <div className="mt-2 text-xs text-center text-primary-700">
          {mode === "start" ? "Kliknij datę przyjazdu" : "Kliknij datę wyjazdu"}
        </div>
      </div>

      {/* Kalendarz */}
      <div className="p-2">
        <DayPicker
          mode="range"
          locale={pl}
          weekStartsOn={1}
          selected={range}
          onDayClick={handleDayClick}
          numberOfMonths={2}
          showOutsideDays
          disabled={{ before: new Date() }}
          modifiers={{
            range_start: startDateObj,
            range_end: endDateObj,
            range_middle:
              range?.from && range?.to
                ? (day: Date) => {
                    if (!range.from || !range.to) return false;
                    return day > range.from && day < range.to;
                  }
                : undefined,
          }}
          modifiersStyles={{
            range_start: { backgroundColor: "#3b82f6", color: "white" },
            range_end: { backgroundColor: "#3b82f6", color: "white" },
            range_middle: { backgroundColor: "#dbeafe", color: "#1e40af" },
          }}
        />
      </div>
    </div>
  );
};

export default DateRangePicker;
